import moment from 'moment';
import { DashboardAction } from '../actions/dashboard';
import { APIError } from '../APIError';
import { DEFAULT_SECURITY_SCORE, getObjVal, isExpired } from '../constants';
import { SecurityInvestigationsEntry } from '../routes/dashboard/SecurityInvestigations';
import { TopCountriesEntry } from '../routes/dashboard/TopCountriesMap';
import { ConfigurationEntry, Store } from '../types';

export type SensorMetricsMap = {
  [key: string]: Array<{ x: number; y: number }>;
};

export type State = {
  summary: {
    readonly loading: boolean;
    readonly areas: Array<ConfigurationEntry>;
    readonly configuration: number;
    readonly securityScore: string;
    readonly error: APIError | null;
    readonly lastFetched: moment.Moment | null;
  };
  incidents: {
    readonly loading: boolean;
    readonly investigations: Array<SecurityInvestigationsEntry>;
    readonly ticketed: number | null;
    readonly total: number | null;
    readonly error: APIError | null;
    readonly lastFetched: moment.Moment | null;
  };
  countries: {
    readonly loading: boolean;
    readonly topCountries: Array<TopCountriesEntry>;
    readonly error: APIError | null;
    readonly lastFetched: moment.Moment | null;
  };
  sensors: {
    readonly loading: boolean;
    readonly sensorMetrics: SensorMetricsMap;
    error: APIError | null;
    readonly lastFetched: moment.Moment | null;
  };
};

export const INITIAL_DASHBOARD_STATE: State = {
  summary: {
    loading: false,
    areas: [],
    configuration: 0,
    securityScore: DEFAULT_SECURITY_SCORE,
    error: null,
    lastFetched: null,
  },
  incidents: {
    loading: false,
    investigations: [],
    ticketed: null,
    total: null,
    error: null,
    lastFetched: null,
  },
  countries: {
    loading: false,
    topCountries: [],
    error: null,
    lastFetched: null,
  },
  sensors: {
    loading: false,
    sensorMetrics: {},
    error: null,
    lastFetched: null,
  },
};

const dashboardReducer = (
  state: State = INITIAL_DASHBOARD_STATE,
  action: DashboardAction,
): State => {
  switch (action.type) {
    case 'set_dashboard_loading':
      const existing = state[action.metric];
      return {
        ...state,
        [action.metric]: { ...existing, loading: true },
      };

    case 'set_incidents_dashboard_data':
      return {
        ...state,
        incidents: {
          loading: false,
          investigations: getObjVal(action.metrics, 'histogram', []),
          total: getObjVal(action.metrics, 'total', null),
          ticketed: getObjVal(action.metrics, 'ticketed', null),
          error: null,
          lastFetched: moment(),
        },
      };

    case 'set_country_flows_dashboard_data':
      return {
        ...state,
        countries: {
          loading: false,
          topCountries: getObjVal(action.metrics, 'countries', []),
          error: null,
          lastFetched: moment(),
        },
      };

    case 'set_summary_dashboard_data':
      return {
        ...state,
        summary: {
          loading: false,
          areas: getObjVal(action.metrics, 'configuration.areas', []),
          configuration: parseInt(
            getObjVal(action.metrics, 'configuration.score', 0),
            10,
          ),
          securityScore: action.metrics.security_score,
          error: null,
          lastFetched: moment(),
        },
      };

    case 'set_sensor_metrics':
      if (!Array.isArray(action.sensors)) {
        return state;
      }

      return {
        ...state,
        sensors: {
          sensorMetrics: action.sensors.reduce(
            (obj, sensor) => ({ ...obj, [sensor.id]: sensor.obs }),
            {},
          ),
          loading: false,
          error: null,
          lastFetched: moment(),
        },
      };

    case 'set_dashboard_incidents_error':
      return {
        ...state,
        incidents: {
          ...INITIAL_DASHBOARD_STATE.incidents,
          loading: false,
          error: action.err,
        },
      };

    case 'set_dashboard_country_flows_error':
      return {
        ...state,
        countries: {
          ...INITIAL_DASHBOARD_STATE.countries,
          loading: false,
          error: action.err,
        },
      };

    case 'set_dashboard_summary_error':
      return {
        ...state,
        summary: {
          ...INITIAL_DASHBOARD_STATE.summary,
          loading: false,
          error: action.err,
        },
      };

    case 'set_sensor_metrics_error':
      return {
        ...state,
        sensors: {
          ...INITIAL_DASHBOARD_STATE.sensors,
          loading: false,
          error: action.err,
        },
      };

    default:
      return state;
  }
};

export default dashboardReducer;

// ------------
// Selectors
// -------------
export const dashboardSelector = {
  isLoadingCountryFlows: (store: Store): boolean =>
    store.dashboard.countries.loading,
  isLoadingIncidents: (store: Store): boolean =>
    store.dashboard.incidents.loading,
  isLoadingSensors: (store: Store): boolean => store.dashboard.sensors.loading,
  isLoadingSummary: (store: Store): boolean => store.dashboard.summary.loading,
  isExpiredCountryFlows: (store: Store): boolean =>
    isExpired(store.dashboard.countries.lastFetched, 15),
  isExpiredIncidents: (store: Store): boolean =>
    isExpired(store.dashboard.incidents.lastFetched, 5),
  isExpiredSensors: (store: Store): boolean =>
    isExpired(store.dashboard.sensors.lastFetched, 5),
  isExpiredSummary: (store: Store): boolean =>
    isExpired(store.dashboard.summary.lastFetched, 10),
  getAreas: (store: Store): Array<ConfigurationEntry> =>
    store.dashboard.summary.areas,
  getCountryFlowsError: (store: Store): APIError | null =>
    store.dashboard.countries.error,
  getIncidentsError: (store: Store): APIError | null =>
    store.dashboard.incidents.error,
  getInvestigations: (store: Store): Array<SecurityInvestigationsEntry> =>
    store.dashboard.incidents.investigations,
  getSecurityScore: (store: Store): string =>
    store.dashboard.summary.securityScore,
  getSensorMetricsError: (store: Store): APIError | null =>
    store.dashboard.sensors.error,
  getSensorMetrics: (store: Store): SensorMetricsMap =>
    store.dashboard.sensors.sensorMetrics,
  getSummaryError: (store: Store): APIError | null =>
    store.dashboard.summary.error,
  getInvestigationCount: (store: Store): number | null =>
    store.dashboard.incidents.total,
  getTicketedInvestigationCount: (store: Store): number | null =>
    store.dashboard.incidents.ticketed,
  getTopCountries: (store: Store): Array<TopCountriesEntry> =>
    store.dashboard.countries.topCountries,
};
